<template>
  <ForgotPasswordModal
    :visible="forgotPasswordModalIsVisible"
    @onPasswordReset="onPasswordReset"
    @onClose="onClosePasswordResetModal"
  />
  <div class="flex flex-col min-h-full sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-md pt-28 mt-2">
      <div class="px-4 bg-white shadow sm:rounded-lg sm:px-10">
        <div class="py-2">
          <div class="py-2">
            <EZRackLogo/>
          </div>
          <Divider/>
          <div class="py-2 text-center text-lg font-semibold text-gray-500">Sign In</div>
          <div class="pt-3 pb-2">
            <RMSInputText
              :fieldId="'preferred_username'"
              :config="{ label: 'Username' }"
              :originalValue="usernameOriginalValue"
              @onValidInput="onValidInputUsername"
              @onClear="onClearInputUsername"
            />
          </div>

          <div class="pt-2 pb-3">
            <RMSFieldLabel
              :fieldId="'password'"
              :fieldLabel="'Password'"
              :config="{ showAvailable: false }"
            />
            <Password
              :id="'password'"
              v-model="formData.password"
              toggleMask
              :feedback="false"
              :class="'w-full h-[2.65rem]'"
              :inputClass="'w-full'"
            />
          </div>

          <div class="pt-14 pb-4">
            <button
              :disabled="!isFormValid"
              class="w-full sign-in-button font-medium"
              @click="onSubmit"
            >Sign In
            </button>
          </div>

          <div class="flex items-center text-md py-4">
            <a class="text-gray-500 hover:cursor-pointer" @click="onClickForgotPassword"> Forgot your password? </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import RMSInputText from '@/components/fields/RMSInputText.vue'
import RMSFieldLabel from '@/components/fields/RMSFieldLabel.vue'
import ForgotPasswordModal from '@/components/auth/ForgotPasswordModal.vue'
import Password from 'primevue/password'
import AuthenticationService from '@/services/auth/AuthenticationService'
import authenticationErrors from '@/contracts/AuthenticationErrors'
import EZRackLogo from '@/components/design/EZRackLogo.vue'
import Divider from 'primevue/divider'

export default {
  components: {
    EZRackLogo,
    Divider,
    Password,
    RMSFieldLabel,
    RMSInputText,
    ForgotPasswordModal,
  },
  emits: ['onAuthenticated'],
  setup(props, { emit }) {
    const colorMode = ref('dark_sans_company')
    const forgotPasswordModalIsVisible = ref(false)
    const toast = useToast()
    const authenticationService = new AuthenticationService()
    const usernameOriginalValue = ref('')
    const formData = ref({
      preferred_username: undefined,
      password: undefined,
    })

    const isFormValid = computed(() => {
      return !!formData.value.preferred_username && !!formData.value.password
    })

    const onValidInputUsername = (value) => formData.value.preferred_username = value
    const onClearInputUsername = () => formData.value.preferred_username = undefined

    const onSubmit = () => {
      authenticationService.login(formData.value)
        .then(response => !!response.success ? emit('onAuthenticated') : showError(response))
        .catch(error => showError(error.response))
    }

    const onClickForgotPassword = () => forgotPasswordModalIsVisible.value = true

    const onPasswordReset = () => {
      forgotPasswordModalIsVisible.value = false
      showSuccess('A password reset email has been sent to the email address on file.')
    }

    const onClosePasswordResetModal = () => forgotPasswordModalIsVisible.value = false

    const showSuccess = (message) => {
      toast.add({
        severity: 'success',
        detail: message,
        life: 5000,
      })
    }

    /**
     * @param {object} response
     */
    const showError = (response) => {
      let detail, severity, summary, life
      if (!!response.code) {
        switch (response.code) {
          case authenticationErrors.PasswordMissing:
            severity = 'info'
            summary = 'Alert'
            detail = 'Password reset is required. A password reset email has been sent to the email address on file.'
            life = undefined
            break
          case authenticationErrors.FailedUserLookup:
          case authenticationErrors.FailedAttempt:
            severity = 'info'
            summary = ''
            detail = 'The provided credentials do not match our records.'
            life = 3000
            break
          default:
            severity = 'error'
            summary = 'Error'
            detail = response.message ?? 'An error has occurred. Please contact your system administrator.'
            life = 3000
            break
        }
      } else if (!!response?.data?.message) {
        severity = 'error'
        summary = 'Error'
        detail = response?.data?.message
        life = 3000
      }
      toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: life,
      })
    }

    return {
      usernameOriginalValue,
      formData,
      onValidInputUsername,
      onClearInputUsername,
      isFormValid,
      onSubmit,
      showError,
      onClickForgotPassword,
      onClosePasswordResetModal,
      onPasswordReset,
      forgotPasswordModalIsVisible,
      colorMode,
    }
  },
}
</script>
<style lang="scss">

.sign-in-button,
.sign-in-button:disabled,
.sign-in-button:hover {
  border-radius: 3px;
  background-color: #0a6f3f;
  color: white;
  font-size: 1rem;
  height: 3.6rem;
}

.sign-in-button:disabled {
  opacity: 1;
}

.sign-in-button:hover {
  opacity: 0.95;
}

</style>
